import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSEO } from '@/hooks/useSEO';

const brands = [
  { name: 'Frisokar', category: 'Cadeiras de Escritório', color: 'bg-orange-50', accent: '#E8610A', icon: 'ri-armchair-line' },
  { name: 'Plaxmetal', category: 'Cadeiras Corporativas', color: 'bg-[#f5f5f0]', accent: '#374151', icon: 'ri-armchair-line' },
  { name: 'Marzo Vitorino', category: 'Mesas Corporativas', color: 'bg-[#f7f4f0]', accent: '#92400e', icon: 'ri-layout-grid-line' },
  { name: 'W3 Indústria', category: 'Mobiliários de Aço', color: 'bg-[#f2f2f0]', accent: '#374151', icon: 'ri-archive-line' },
];

const DRIVE_LINK = 'https://drive.google.com/drive/folders/1TbDwhq3iakn4_N3Yj_fQ6eaMwSTHLW5T?usp=sharing';

export default function CatalogosPage() {
  useSEO({
    title: 'Catálogos de Móveis Corporativos | Frisokar, Plaxmetal, Marzo Vitorino, W3 – Home Office Premium',
    description: 'Baixe os catálogos completos de móveis corporativos da Home Office Premium: Frisokar, Plaxmetal, Marzo Vitorino e W3 Indústria. Especificações técnicas, medidas e opções de cada produto.',
    keywords: 'catálogos móveis corporativos, Frisokar catálogo, Plaxmetal catálogo, Marzo Vitorino catálogo, W3 catálogo',
    path: '/catalogos',
    ogTitle: 'Catálogos de Móveis Corporativos | Home Office Premium',
    ogDescription: 'Catálogos completos Frisokar, Plaxmetal, Marzo Vitorino e W3 Indústria. Especificações técnicas e medidas.',
  });

  useEffect(() => {
    const schemaId = 'schema-catalogos';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) { script = document.createElement('script'); script.id = schemaId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Catálogos – Home Office Premium',
      description: 'Catálogos completos de móveis corporativos: Frisokar, Plaxmetal, Marzo Vitorino e W3 Indústria.',
      url: `${import.meta.env.VITE_SITE_URL ?? ''}/catalogos`,
    });

    const breadcrumbId = 'schema-catalogos-breadcrumb';
    let bcScript = document.getElementById(breadcrumbId) as HTMLScriptElement | null;
    if (!bcScript) { bcScript = document.createElement('script'); bcScript.id = breadcrumbId; bcScript.type = 'application/ld+json'; document.head.appendChild(bcScript); }
    bcScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: import.meta.env.VITE_SITE_URL ?? '' },
        { '@type': 'ListItem', position: 2, name: 'Catálogos', item: `${import.meta.env.VITE_SITE_URL ?? ''}/catalogos` },
      ],
    });
    const faqId = 'schema-catalogos-faq';
    let faqScript = document.getElementById(faqId) as HTMLScriptElement | null;
    if (!faqScript) { faqScript = document.createElement('script'); faqScript.id = faqId; faqScript.type = 'application/ld+json'; document.head.appendChild(faqScript); }
    faqScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Como acessar os catálogos de móveis corporativos da Home Office Premium?',
          acceptedAnswer: { '@type': 'Answer', text: 'Os catálogos completos da Frisokar, Plaxmetal, Marzo Vitorino e W3 Indústria estão disponíveis em nossa pasta do Google Drive. Acesse gratuitamente pelo link na página de catálogos do site.' },
        },
        {
          '@type': 'Question',
          name: 'Os catálogos mostram preços dos móveis corporativos?',
          acceptedAnswer: { '@type': 'Answer', text: 'Os catálogos técnicos mostram especificações, medidas e opções de cada produto. Para preços atualizados, entre em contato com nossa equipe pelo WhatsApp ou formulário de contato. Respondemos em até 24 horas.' },
        },
        {
          '@type': 'Question',
          name: 'A Home Office Premium tem catálogo de cadeiras ergonômicas Frisokar?',
          acceptedAnswer: { '@type': 'Answer', text: 'Sim. O catálogo completo da Frisokar está disponível em nossa pasta, com todos os modelos de cadeiras ergonômicas, especificações técnicas, opções de cores e tecidos.' },
        },
        {
          '@type': 'Question',
          name: 'Posso solicitar um orçamento após ver o catálogo?',
          acceptedAnswer: { '@type': 'Answer', text: 'Sim. Após escolher os produtos no catálogo, entre em contato pelo WhatsApp (81) 98203-0285 informando o nome ou código do produto. Nossa equipe prepara um orçamento personalizado com entrega e montagem em Caruaru e todo Pernambuco.' },
        },
      ],
    });

    return () => { script?.remove(); bcScript?.remove(); faqScript?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-20">
        <div className="bg-[#f8f8f6] py-16">
          <div className="max-w-7xl mx-auto px-6">
            <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-3">Downloads</p>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Catálogos</h1>
            <p className="text-gray-500 text-base max-w-xl">Acesse nossa pasta completa com os catálogos de todas as marcas. Especificações técnicas, medidas e opções de cada produto.</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="bg-[#E8610A] rounded-3xl overflow-hidden mb-16 relative">
            <div className="absolute inset-0">
              <img src="/media/ia/0193.webp" alt="Catálogos" loading="eager" fetchPriority="high" decoding="sync" className="w-full h-full object-cover object-top opacity-20" />
            </div>
            <div className="relative z-10 px-8 md:px-16 py-14 flex flex-col md:flex-row items-center justify-between gap-10">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-2xl">
                    <i className="ri-folder-open-line text-white text-2xl"></i>
                  </div>
                  <span className="text-white/70 text-sm font-medium">Google Drive</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Todos os Catálogos</h2>
                <p className="text-white/70 text-base max-w-lg leading-relaxed">Acesse a pasta completa com os catálogos da Frisokar, Plaxmetal, Marzo Vitorino e W3 Indústria.</p>
              </div>
              <a href={DRIVE_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-white text-[#E8610A] font-bold px-10 py-5 rounded-full hover:bg-gray-100 transition-colors duration-200 whitespace-nowrap cursor-pointer flex-shrink-0 text-base">
                <i className="ri-folder-open-line text-lg"></i>
                Acessar Catálogos
              </a>
            </div>
          </div>

          <div className="mb-10">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Marcas disponíveis na pasta</h3>
            <p className="text-gray-500 text-sm mb-8">Os catálogos abaixo estão incluídos na pasta do Google Drive.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {brands.map(brand => (
                <div key={brand.name} className={`${brand.color} rounded-2xl p-6 flex flex-col gap-4`}>
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-white/70">
                    <i className={`${brand.icon} text-xl`} style={{ color: brand.accent }}></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: brand.accent }}>{brand.category}</p>
                    <h4 className="text-gray-900 font-bold text-base">{brand.name}</h4>
                  </div>
                  <a href={DRIVE_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-medium whitespace-nowrap cursor-pointer hover:gap-3 transition-all duration-200 mt-auto" style={{ color: brand.accent }}>
                    Ver catálogo <i className="ri-external-link-line text-sm"></i>
                  </a>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#f8f8f6] rounded-2xl p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 rounded-xl flex-shrink-0">
              <i className="ri-information-line text-[#E8610A] text-xl"></i>
            </div>
            <div>
              <h4 className="text-gray-900 font-semibold mb-1">Precisa de ajuda para escolher?</h4>
              <p className="text-gray-500 text-sm leading-relaxed">Nossa equipe está pronta para te ajudar a encontrar o produto ideal. Entre em contato pelo WhatsApp ou solicite um orçamento personalizado.</p>
            </div>
            <a href="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-[#C4500A] transition-colors duration-200 whitespace-nowrap cursor-pointer flex-shrink-0">
              <i className="ri-whatsapp-line"></i>
              Falar no WhatsApp
            </a>
          </div>
        </div>
      </div>
      <Footer />
      <a
        href="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200"
        aria-label="WhatsApp"
      >
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
